import { useNavigate } from "@tanstack/react-router";
import type { SetStateAction } from "jotai";
import {
  BarChart3Icon,
  BellIcon,
  CogIcon,
  FileCheckIcon,
  FlaskConicalIcon,
  HelpCircleIcon,
  KanbanSquareIcon,
  LayersIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MoonIcon,
  SearchIcon,
  SquarePenIcon,
  SunIcon,
  SunMoonIcon,
  UserIcon,
} from "lucide-react";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

import { hasRequiredPermissions } from "@blis/shared/permissions";

import { PermissionGate } from "@/components/PermissionGate";
import { Badge } from "@/components/ui/badge";
import { CommandMenu as Command } from "@/components/ui/command";
import { Loader } from "@/components/ui/loader";
import { useTheme } from "@/context/theme";
import {
  AccessionSearchCommand,
  accessionSearchPage,
} from "@/features/accession/components/commands/AccessionSearchCommand";
import { logout } from "@/features/auth/api";
import { useAuth } from "@/features/auth/hooks";
import { navigateAndShowTestRunDialog } from "@/features/runs/components/store";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { RoleCondition } from "./RoleCondition";

type CommandMenuContextValue = {
  contextual: ReactNode[];
  setContextual: (items: ReactNode[]) => void;
  page: CommandPage;
  pages: CommandPage[];
  setPages: Dispatch<SetStateAction<CommandPage[]>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
};

type CommandPage = {
  id: string | number;
  label: string;
  searchPlaceholder?: string;
  shouldFilter?: boolean;
  hideEmpty?: boolean;
};

const CommandMenuContext = createContext({} as CommandMenuContextValue);

export function CommandMenuProvider({ children }: { children: ReactNode }) {
  const [contextual, setContextual] = useState<ReactNode[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState<CommandPage[]>([]);
  const [search, setSearch] = useState("");

  const page = pages[pages.length - 1];

  const setOpenWrapper: Dispatch<SetStateAction<boolean>> = useCallback(
    (flag) => {
      if (!flag) {
        // wait for the modal to finish close animation
        setTimeout(() => {
          setPages([]);
        }, 150);
      }
      setSearch("");
      setOpen(flag);
    },
    [],
  );

  return (
    <CommandMenuContext.Provider
      value={{
        contextual,
        setContextual,
        pages,
        setPages: (pages) => {
          setPages(pages);
          setSearch("");
        },
        page,
        search,
        setSearch,
        open,
        setOpen: setOpenWrapper,
        loading,
        setLoading,
      }}
    >
      {children}
    </CommandMenuContext.Provider>
  );
}

export function useCommandMenu() {
  const context = useContext(CommandMenuContext);
  if (!context) {
    throw new Error("useCommandMenu must be used within a CommandMenuProvider");
  }
  return {
    ...context,
  };
}

export function CommandMenu({ children }: { children?: ReactNode }) {
  const {
    contextual,
    page,
    pages,
    setPages,
    search,
    setSearch,
    open,
    setOpen,
    loading,
  } = useContext(CommandMenuContext);

  const ref = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  // useEffect(() => {
  //   if (ref.current) {
  //     // to animate the bounce
  //     ref.current.style.transition = "transform 0.1s ease-in-out";
  //   }
  // }, []);

  function bounce() {
    if (ref.current) {
      ref.current.style.transform = "scale(0.97)";
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.transform = "";
        }
      }, 100);

      setSearch("");
    }
  }

  useTinyKeys(
    {
      "$mod+k": (evt) => {
        evt.preventDefault();
        setOpen(true);
      },
    },
    [],
    false,
  );

  useTinyKeys(
    {
      "/": (evt) => {
        evt.preventDefault();
        setPages([accessionSearchPage]);
        setOpen(true);
      },
      "c a": (evt) => {
        evt.preventDefault();
        navigate({ to: "/accessions/new" });
      },
      "c u": (evt) => {
        evt.preventDefault();
        navigateAndShowTestRunDialog({ navigate, location });
      },
      "g a": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/accessions" });
      },
      "g b": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/board" });
      },
      "g d": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/admin" });
      },
      "g h": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/" });
      },
      "g p": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/auth/profile" });
      },
      "g r": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/reports" });
      },
      "g i": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        if (
          !hasRequiredPermissions(user.role, {
            requiredPermission: "read:insights",
          })
        ) {
          return;
        }
        navigate({ to: "/insights/explore" });
      },
      "g s": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/search" });
      },
      "g u": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        navigate({ to: "/runs" });
      },
      "s o": (evt) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        logout();
      },
      "Shift+?": (evt) => {
        evt.preventDefault();
        navigate({ to: "/help" });
      },
    },
    [navigate, user.role],
    true,
  );

  return (
    <Command
      isOpen={open}
      onOpenChange={setOpen}
      commandRef={ref}
      shouldFilter={page?.shouldFilter ?? true}
      onKeyDown={(e) => {
        // Escape goes to previous page
        // Backspace goes to previous page when search is empty
        if (e.key === "Enter") {
          bounce();
        }
        if (
          (e.key === "Backspace" && !search) ||
          (e.key === "Escape" && page)
        ) {
          e.preventDefault();
          bounce();
          setPages((pages) => pages.slice(0, -1));
        }
        if (e.key === "Escape" && !page) {
          setOpen(false);
        }
      }}
    >
      {pages.length > 0 && (
        <div className="m-2 flex items-center gap-1">
          {pages.map((page) => (
            <Badge size="sm" key={page.id}>
              {page.label}
            </Badge>
          ))}
        </div>
      )}

      <Command.Input
        placeholder={page?.searchPlaceholder ?? "Type a command or search..."}
        value={search}
        onValueChange={setSearch}
        icon={loading ? <Loader className="mr-2" /> : undefined}
      />
      <Command.List>
        {page?.shouldFilter !== false && !page?.hideEmpty && (
          <Command.Empty>No results found.</Command.Empty>
        )}
        {contextual}
        {children}
        {!page && (
          <>
            <Command.Section heading="Common actions">
              <Command.Item
                value="search accessions"
                onSelect={() => {
                  setPages([accessionSearchPage]);
                }}
              >
                <SearchIcon />
                <span>Search accessions</span>
                <Command.Shortcut>/</Command.Shortcut>
              </Command.Item>
              <Command.Item
                value="create accession"
                onSelect={() => navigate({ to: "/accessions/new" })}
              >
                <SquarePenIcon />
                <span>Create new accession</span>
                <Command.Shortcut>C A</Command.Shortcut>
              </Command.Item>
              <RoleCondition
                roles={[
                  "blis_lab_manager",
                  "blis_lab_tech",
                  "blis_lab_assistant",
                ]}
              >
                <Command.Item
                  value="create test run"
                  onSelect={() =>
                    navigateAndShowTestRunDialog({ navigate, location })
                  }
                >
                  <FlaskConicalIcon />
                  <span>Create test run</span>
                  <Command.Shortcut>C U</Command.Shortcut>
                </Command.Item>
              </RoleCondition>
            </Command.Section>
            <Command.Separator />
            <Command.Section heading="Navigation">
              <Command.Item
                onSelect={() => navigate({ to: "/" })}
                value="go to dashboard"
              >
                <LayoutDashboardIcon />
                <span>Go to dashboard</span>
                <Command.Shortcut>G H</Command.Shortcut>
              </Command.Item>
              <Command.Item
                onSelect={() => navigate({ to: "/accessions" })}
                value="go to accessions"
              >
                <LayersIcon />
                <span>Go to accessions</span>
                <Command.Shortcut>G A</Command.Shortcut>
              </Command.Item>
              <Command.Item
                onSelect={() => navigate({ to: "/board" })}
                value="go to board"
              >
                <KanbanSquareIcon />
                <span>Go to board</span>
                <Command.Shortcut>G B</Command.Shortcut>
              </Command.Item>
              <Command.Item
                onSelect={() => navigate({ to: "/search" })}
                value="go to search"
              >
                <SearchIcon />
                <span>Go to search</span>
                <Command.Shortcut>G S</Command.Shortcut>
              </Command.Item>
              <RoleCondition
                roles={[
                  "blis_lab_manager",
                  "blis_lab_tech",
                  "blis_lab_assistant",
                ]}
              >
                <Command.Item
                  onSelect={() => navigate({ to: "/runs" })}
                  value="go to test runs"
                >
                  <FlaskConicalIcon />
                  <span>Go to test runs</span>
                  <Command.Shortcut>G U</Command.Shortcut>
                </Command.Item>
                <Command.Item
                  onSelect={() => navigate({ to: "/reports" })}
                  value="go to reports"
                >
                  <FileCheckIcon />
                  <span>Go to reports</span>
                  <Command.Shortcut>G R</Command.Shortcut>
                </Command.Item>
              </RoleCondition>
              <PermissionGate requiredPermission="read:insights">
                <Command.Item
                  onSelect={() => navigate({ to: "/insights/explore" })}
                  value="go to explore"
                >
                  <BarChart3Icon />
                  <span>Go to explore</span>
                  <Command.Shortcut>G I</Command.Shortcut>
                </Command.Item>
              </PermissionGate>
              <RoleCondition roles={["blis_lab_manager"]}>
                <Command.Item
                  onSelect={() => navigate({ to: "/admin" })}
                  value="go to admin"
                >
                  <CogIcon />
                  <span>Go to admin</span>
                  <Command.Shortcut>G D</Command.Shortcut>
                </Command.Item>
              </RoleCondition>
              <Command.Item
                onSelect={() => navigate({ to: "/help" })}
                value="help and support"
              >
                <HelpCircleIcon />
                <span>Help and support</span>
                <Command.Shortcut>?</Command.Shortcut>
              </Command.Item>
              <RoleCondition
                roles={[
                  "blis_lab_manager",
                  "blis_lab_tech",
                  "blis_lab_assistant",
                ]}
              >
                <Command.Item
                  onSelect={() => navigate({ to: "/help/notifications" })}
                  value="check notification status"
                >
                  <BellIcon />
                  <span>Check notification status</span>
                </Command.Item>
              </RoleCondition>
            </Command.Section>
            <Command.Section heading="User">
              <Command.Item
                onSelect={() => navigate({ to: "/auth/profile" })}
                value="my profile"
              >
                <UserIcon />
                <span>My profile</span>
                <Command.Shortcut>G P</Command.Shortcut>
              </Command.Item>
              <Command.Item
                onSelect={() => {
                  setPages([{ id: "theme", label: "Change theme" }]);
                }}
              >
                <SunMoonIcon />
                <span>Change theme</span>
              </Command.Item>
              <Command.Item onSelect={() => logout()} value="sign out logout">
                <LogOutIcon />
                <span>Sign out</span>
                <Command.Shortcut>S O</Command.Shortcut>
              </Command.Item>
            </Command.Section>
          </>
        )}
        {page?.id === "search" && <AccessionSearchCommand />}
        {page?.id === "theme" && <ThemeCommands />}
      </Command.List>
    </Command>
  );
}

function ThemeCommands() {
  const { theme, setTheme } = useTheme();
  const { setOpen } = useCommandMenu();

  return (
    <Command.Section heading="Theme">
      <Command.Item
        value="light theme"
        onSelect={() => {
          setTheme("light");
          setOpen(false);
        }}
        disabled={theme === "light"}
      >
        <SunIcon />
        <span>Light theme</span>
      </Command.Item>
      <Command.Item
        value="dark theme"
        onSelect={() => {
          setTheme("dark");
          setOpen(false);
        }}
        disabled={theme === "dark"}
      >
        <MoonIcon />
        <span>Dark theme</span>
      </Command.Item>
      <Command.Item
        value="system theme"
        onSelect={() => {
          setTheme("system");
          setOpen(false);
        }}
        disabled={theme === "system"}
      >
        <SunMoonIcon />
        <span>System theme</span>
      </Command.Item>
    </Command.Section>
  );
}
